/**
 * Iter123 — Public Tip Portal.
 *
 * No authentication required. Rendered under /tip.
 * Submits to POST /api/public/tips. Anonymous option omits reporter fields.
 */
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Button } from '../components/ui/button';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from '../hooks/use-toast';
import { BACKEND_URL } from '../config/canonical';
import { Shield, Send, Loader2, CheckCircle2, Info } from 'lucide-react';

const CATEGORIES = [
  'SUSPECTED_CRIMINAL_ACTIVITY', 'MISSING_PERSON', 'STOLEN_VEHICLE',
  'FRAUD', 'FUGITIVE_LOCATION', 'DRUG_ACTIVITY', 'HARASSMENT', 'OTHER',
];

const initial = {
  anonymous: true,
  reporter_name: '', reporter_email: '', reporter_phone: '',
  subject_name: '', subject_description: '',
  incident_location: '', incident_when: '',
  category: 'SUSPECTED_CRIMINAL_ACTIVITY',
  summary: '',
};

export default function PublicTipPortal() {
  const [f, setF] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [tipId, setTipId] = useState(null);

  const set = (k, v) => setF(prev => ({ ...prev, [k]: v }));

  const submit = async () => {
    if (f.summary.trim().length < 15) {
      return toast({ title: 'Add more detail', description: 'Please describe the situation with at least 15 characters.', variant: 'destructive' });
    }
    setBusy(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/public/tips`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(f),
      });
      const d = await r.json();
      if (!r.ok) return toast({ title: 'Submission failed', description: d.detail || 'Error', variant: 'destructive' });
      setTipId(d.tip_id);
      toast({ title: 'Tip received', description: 'An investigator will review your submission.' });
    } finally { setBusy(false); }
  };

  if (tipId) {
    return (
      <div className="min-h-screen bg-[#0B1220] flex items-center justify-center px-4" data-testid="public-tip-success">
        <div className="max-w-md w-full bg-[#131A2A] border border-emerald-500/40 rounded-lg p-8 text-center">
          <CheckCircle2 className="w-14 h-14 text-emerald-400 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-white mb-2">Thank You</h1>
          <p className="text-slate-300 mb-4">Your tip has been received and will be reviewed by our team.</p>
          <div className="text-xs text-slate-400 font-mono">Reference: <span className="text-amber-300">{tipId}</span></div>
          <p className="text-xs text-slate-500 mt-6">If your submission is urgent or involves an emergency, please call 911.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0B1220] py-10 px-4" data-testid="public-tip-page">
      <div className="max-w-2xl mx-auto">
        <div className="flex items-center justify-start mb-4">
          <Link to="/login">
            <Button type="button" variant="outline"
              className="h-9 border-amber-500/40 !text-amber-200 hover:!bg-amber-500/10 font-mono uppercase tracking-wider text-[11px]"
              data-testid="tip-back-to-login">
              ← Back to Login
            </Button>
          </Link>
        </div>
        <div className="flex items-center gap-3 mb-6">
          <div className="w-14 h-14 rounded-lg bg-amber-500/20 border border-amber-500/40 flex items-center justify-center">
            <Shield className="w-8 h-8 text-amber-300" />
          </div>
          <div>
            <div className="text-xs text-amber-300 uppercase tracking-widest font-mono">NexusTrac · Public Tip Portal</div>
            <h1 className="text-3xl font-bold text-white">Submit an Information Tip</h1>
            <p className="text-slate-400 text-sm mt-1">All submissions are reviewed by professional investigators. Anonymous option available.</p>
          </div>
        </div>

        <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3 mb-6 flex items-start gap-2">
          <Info className="w-5 h-5 text-blue-300 flex-shrink-0 mt-0.5" />
          <div className="text-xs text-blue-200">
            <b>Emergency?</b> If a crime is in progress or a life is in danger, call 911 immediately.
            This portal is for non-emergency intel.
          </div>
        </div>

        <div className="bg-[#131A2A] border border-slate-700 rounded-lg p-6 space-y-4">
          <label className="flex items-start gap-2 cursor-pointer">
            <Checkbox checked={f.anonymous} onCheckedChange={v => set('anonymous', !!v)} data-testid="tip-anonymous" />
            <span className="text-sm text-slate-200">Submit anonymously (no personal info will be recorded)</span>
          </label>

          {!f.anonymous && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              <Input value={f.reporter_name} onChange={e => set('reporter_name', e.target.value)} placeholder="Your name (optional)" className="bg-slate-800 border-slate-600 text-white" data-testid="tip-reporter-name" />
              <Input value={f.reporter_email} onChange={e => set('reporter_email', e.target.value)} placeholder="Email (optional)" className="bg-slate-800 border-slate-600 text-white" data-testid="tip-reporter-email" />
              <Input value={f.reporter_phone} onChange={e => set('reporter_phone', e.target.value)} placeholder="Phone (optional)" className="bg-slate-800 border-slate-600 text-white" data-testid="tip-reporter-phone" />
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-slate-400">Category</label>
              <Select value={f.category} onValueChange={v => set('category', v)}>
                <SelectTrigger className="bg-slate-800 border-slate-600 text-white" data-testid="tip-category"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map(c => <SelectItem key={c} value={c}>{c.replace(/_/g, ' ')}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-slate-400">When did it happen?</label>
              <Input value={f.incident_when} onChange={e => set('incident_when', e.target.value)}
                placeholder="e.g. yesterday around 9pm" className="bg-slate-800 border-slate-600 text-white" data-testid="tip-when" />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Input value={f.subject_name} onChange={e => set('subject_name', e.target.value)}
              placeholder="Subject name (if known)" className="bg-slate-800 border-slate-600 text-white" data-testid="tip-subject-name" />
            <Input value={f.incident_location} onChange={e => set('incident_location', e.target.value)}
              placeholder="Location / address" className="bg-slate-800 border-slate-600 text-white" data-testid="tip-location" />
          </div>

          <div>
            <label className="text-xs text-slate-400">Description of subject (physical, vehicle, associates)</label>
            <Input value={f.subject_description} onChange={e => set('subject_description', e.target.value)}
              className="bg-slate-800 border-slate-600 text-white" data-testid="tip-subject-desc" />
          </div>

          <div>
            <label className="text-xs text-slate-400">Detailed summary *</label>
            <Textarea value={f.summary} onChange={e => set('summary', e.target.value)}
              rows={5} placeholder="Describe what you saw, heard, or know. Include as much detail as possible."
              className="bg-slate-800 border-slate-600 text-white" data-testid="tip-summary" />
            <div className="text-[10px] text-slate-500 mt-1">{f.summary.length} characters (minimum 15)</div>
          </div>

          <div className="pt-2 flex justify-end">
            <Button onClick={submit} disabled={busy || f.summary.trim().length < 15}
              className="bg-amber-500 hover:bg-amber-400 !text-slate-900 font-semibold" data-testid="tip-submit-btn">
              {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Send className="w-4 h-4 mr-1" />}
              Submit Tip
            </Button>
          </div>
        </div>

        <p className="text-center text-[10px] text-slate-500 mt-6">
          NexusTrac Investigations · Your tip will be reviewed within 24 hours.
        </p>
      </div>
    </div>
  );
}
